import { useState } from 'react'
import { UserPlus, X } from 'lucide-react'
import { Button, Input, Modal, Text } from '../../ui'
import { SettingsSection } from './SettingsSection'
import { useInviteMember, useRemoveMember, useTeamMembers } from '../../../hooks/useTeam'
import type { Workspace } from '../../../lib/api'

type TeamSectionProps = {
  workspace: Workspace
  isAdmin: boolean
}

export function TeamSection({ workspace, isAdmin }: TeamSectionProps) {
  const { data: membersResult, isLoading } = useTeamMembers(workspace.id)
  const inviteMember = useInviteMember(workspace.id)
  const removeMember = useRemoveMember(workspace.id)

  const [inviteOpen, setInviteOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<'admin' | 'editor'>('editor')
  const [inviteError, setInviteError] = useState<string | null>(null)
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null)
  const [removingId, setRemovingId] = useState<string | null>(null)

  const members = membersResult?.success ? membersResult.data : []

  async function handleInvite(event: React.FormEvent) {
    event.preventDefault()
    setInviteError(null)
    setInviteSuccess(null)

    const result = await inviteMember.mutateAsync({ email, role })
    if (!result.success) {
      setInviteError(result.error.message)
      return
    }

    setInviteSuccess(`${result.data.name || email} has been added.`)
    setEmail('')
    setRole('editor')
    setInviteOpen(false)
  }

  async function handleRemove(userId: string) {
    setRemovingId(userId)
    await removeMember.mutateAsync(userId)
    setRemovingId(null)
  }

  return (
    <SettingsSection
      id="team"
      title="Team"
      description="People with access to SonderSoft Knowledge in this workspace."
    >
      <div className="flex flex-col gap-4">
        {inviteSuccess && (
          <Text variant="caption" className="text-green-700">
            {inviteSuccess}
          </Text>
        )}

        {isAdmin && (
          <div className="flex justify-end">
            <Button
              size="sm"
              variant="outline"
              leftIcon={<UserPlus className="h-4 w-4" />}
              onClick={() => setInviteOpen(true)}
            >
              Invite member
            </Button>
          </div>
        )}

        {isLoading ? (
          <Text variant="body-sm" className="text-foreground/50">
            Loading members...
          </Text>
        ) : members.length === 0 ? (
          <Text variant="body-sm" className="text-foreground/50">
            No members with Knowledge access yet.
          </Text>
        ) : (
          <div className="divide-y divide-foreground/10 rounded-xl border border-foreground/10">
            {members.map((member) => (
              <div
                key={member.userId}
                className="flex items-center justify-between gap-3 px-4 py-3"
              >
                <div className="min-w-0">
                  <Text variant="label" className="truncate">
                    {member.name}
                  </Text>
                  <Text variant="caption" className="truncate text-foreground/45">
                    {member.email}
                  </Text>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span className="rounded-full border border-foreground/10 px-2.5 py-1 text-xs capitalize text-foreground/60">
                    {member.role}
                  </span>
                  {isAdmin && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-foreground/40"
                      loading={removingId === member.userId}
                      onClick={() => handleRemove(member.userId)}
                      aria-label={`Remove ${member.name}`}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal open={inviteOpen} onClose={() => setInviteOpen(false)}>
        <form onSubmit={handleInvite} className="flex flex-col gap-4 pt-2">
          <Text variant="h2" className="text-lg">
            Invite to Knowledge
          </Text>
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            error={inviteError ?? undefined}
          />
          <div className="flex flex-col gap-2">
            <Text variant="label">Role</Text>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as 'admin' | 'editor')}
              className="h-11 rounded-full border border-foreground/20 bg-white px-4 text-sm outline-none focus:border-foreground"
            >
              <option value="editor">Editor</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={() => setInviteOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" size="sm" loading={inviteMember.isPending}>
              Send invite
            </Button>
          </div>
        </form>
      </Modal>
    </SettingsSection>
  )
}
